<template>
  <div class="p-4">
    <h2 class="text-xl font-bold mb-2">График: Угол маятника</h2>
    <div class="flex gap-4 mb-4">
      <button @click="connect" class="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition">
        {{ isConnected ? 'Переподключиться' : 'Подключиться к Arduino' }}
      </button>
      <button v-if="isConnected" @click="disconnect" class="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600 transition">
        Отключиться
      </button>
      <button @click="clearChart" class="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600 transition">
        Очистить график
      </button>
    </div>
    <div v-if="error" class="mb-4 p-2 bg-red-100 text-red-700 rounded">{{ error }}</div>
    <div v-if="isConnected" class="mb-2 text-green-600">Подключено</div>
    <div ref="chartContainer" class="ct-chart"></div>
  </div>
</template>

<script>
import * as Chartist from 'chartist'

export default {
  name: 'PendulumArduino',
  data() {
    return {
      chart: null,
      dataPoints: [],
      serialPort: null,
      reader: null,
      isConnected: false,
      error: null,
      reading: false,
      startTime: null
    }
  },
  methods: {
    async connect() {
      try {
        if (!navigator.serial) {
          throw new Error('Web Serial API не поддерживается в вашем браузере')
        }

        if (this.serialPort) await this.disconnect()

        this.serialPort = await navigator.serial.requestPort()
        await this.serialPort.open({ baudRate: 115200 })

        const textDecoder = new TextDecoderStream()
        this.reader = textDecoder.readable.getReader()
        this.serialPort.readable.pipeTo(textDecoder.writable)

        this.isConnected = true
        this.reading = true
        this.startTime = Date.now()
        this.readLoop()

      } catch (err) {
        this.handleError(err)
      }
    },

    async disconnect() {
      try {
        this.reading = false
        if (this.reader) {
          await this.reader.cancel().catch(() => {})
          this.reader = null
        }
        if (this.serialPort) {
          await this.serialPort.close()
          this.serialPort = null
        }
        this.isConnected = false
      } catch (err) {
        this.handleError(err)
      }
    },

    async readLoop() {
      let buffer = ''
      try {
        while (this.reading) {
          const { value, done } = await this.reader.read()
          if (done) break

          buffer += value
          const lines = buffer.split('\n')
          buffer = lines.pop() || ''

          for (const line of lines) {
            if (line.trim()) this.processLine(line.trim())
          }
        }
      } catch (err) {
        if (err.name !== 'InterruptedError') {
          this.handleError(err)
        }
      }
    },

    processLine(line) {
      // Обрабатываем два формата: "ANGLE:XX.XX" и просто число
      let angle = null;
      
      // Проверяем формат "ANGLE:XX.XX"
      const angleMatch = line.match(/ANGLE:([-+]?\d+\.\d{2})/);
      if (angleMatch) {
        angle = parseFloat(angleMatch[1]);
      } 
      // Если нет, пробуем распарсить просто число
      else if (!isNaN(parseFloat(line))) {
        angle = parseFloat(line);
      }

      if (angle !== null && !isNaN(angle)) {
        this.addDataPoint(angle);
      }
    },

    addDataPoint(angle) {
      const now = Date.now() - this.startTime; // Время в мс с момента подключения
      this.dataPoints.push({
        x: now / 1000, // Переводим в секунды
        y: angle
      });

      // Ограничиваем количество точек для производительности
      if (this.dataPoints.length > 200) {
        this.dataPoints.shift();
      }

      this.updateChart();
    },

    updateChart() {
      if (this.chart) {
        this.chart.update({
          series: [{
            name: 'angle',
            data: this.dataPoints
          }]
        });
      }
    },

    clearChart() {
      this.dataPoints = [];
      this.startTime = Date.now();
      this.updateChart();
    },

    handleError(err) {
      console.error('Ошибка:', err);
      this.error = err.message || 'Произошла ошибка';
      this.isConnected = false;
      this.reading = false;
    },

    initChart() {
      this.chart = new Chartist.Line(this.$refs.chartContainer, {
        series: [{
          name: 'angle',
          data: []
        }]
      }, {
        width: '100%',
        height: '500px',
        axisX: {
          type: Chartist.AutoScaleAxis,
          labelInterpolationFnc: (value) => {
            // Форматируем время в секундах как "SS.fff"
            const seconds = Math.floor(value);
            const milliseconds = Math.floor((value - seconds) * 1000);
            return `${seconds}.${milliseconds.toString().padStart(3, '0')}`;
          },
          title: 'Время (с)'
        },
        axisY: {
          title: 'Угол (°)',
          low: -180,
          high: 180
        },
        lineSmooth: false,
        showPoint: false,
        chartPadding: {
          top: 30,
          right: 40,
          bottom: 40,
          left: 50
        }
      });
    }
  },
  mounted() {
    this.initChart();
  },
  beforeUnmount() {
    this.disconnect();
    if (this.chart) {
      this.chart.detach();
    }
  }
}
</script>

<style>
@import '~chartist/dist/chartist.min.css';

.p-4{
  width: 98%;
}

.ct-chart {
  width: 100%;
  height: 500px;
  background-color: #f8fafc;
  border-radius: 0.5rem;
  padding: 1rem;
}

.ct-label {
  font-size: 0.8rem;
  color: #64748b;
}
</style>